import { isClickedOutside } from '@theme/utilities';

const FLOATING_BANNER_SELECTOR = '.floating-banner';
const FLOATING_BANNER_GAP = 12;
const FLOATING_BANNER_FALLBACK_Z_INDEX = 100000;

export class HelpFloatingComponent extends HTMLElement {
  #details = null;
  #floatingBanner = null;
  #documentObserver = null;
  #bannerObserver = null;
  #resizeObserver = null;

  #handleClick = (event) => {
    if (!this.#details?.open) return;

    if (isClickedOutside(event, this)) {
      this.#details.removeAttribute('open');
    }
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#details?.open) {
      event.preventDefault();
      this.#details.removeAttribute('open');
    }
  };

  #handleDocumentMutations = (mutations) => {
    const floatingBannerChanged = mutations.some((mutation) => {
      return [...mutation.addedNodes, ...mutation.removedNodes].some(
        (node) => node.matches?.(FLOATING_BANNER_SELECTOR) || node.querySelector?.(FLOATING_BANNER_SELECTOR),
      );
    });

    if (floatingBannerChanged) this.#syncFloatingBannerPosition();
  };

  #handleWindowResize = () => this.#syncFloatingBannerPosition();

  #resetFloatingBannerPosition() {
    this.style.removeProperty('--help-floating-bottom');
    this.style.removeProperty('--help-floating-z-index');
  }

  #syncFloatingBannerPosition = () => {
    const banner = document.querySelector(FLOATING_BANNER_SELECTOR);

    if (banner !== this.#floatingBanner) {
      this.#bannerObserver?.disconnect();
      this.#resizeObserver?.disconnect();
      this.#floatingBanner = banner;
      if (banner) {
        this.#bannerObserver?.observe(banner, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
        this.#resizeObserver?.observe(banner);
      }
    }

    if (!banner?.matches('.floating-banner.is-ready')) {
      this.#resetFloatingBannerPosition();
      return;
    }

    const bannerStyles = getComputedStyle(banner);
    const bannerRect = banner.getBoundingClientRect();
    const bannerHeight = banner.offsetHeight || bannerRect.height;
    const computedBottom = Number.parseFloat(bannerStyles.bottom);
    const bannerBottom = Number.isFinite(computedBottom)
      ? Math.max(0, computedBottom)
      : Math.max(0, window.innerHeight - bannerRect.bottom);

    if (bannerStyles.display === 'none' || bannerStyles.visibility === 'hidden' || bannerHeight <= 0) {
      this.#resetFloatingBannerPosition();
      return;
    }

    const bannerZIndex = Number.parseInt(bannerStyles.zIndex, 10);
    const helpZIndex = Number.isFinite(bannerZIndex) ? bannerZIndex + 1 : FLOATING_BANNER_FALLBACK_Z_INDEX;

    this.style.setProperty('--help-floating-bottom', `${bannerBottom + bannerHeight + FLOATING_BANNER_GAP}px`);
    this.style.setProperty('--help-floating-z-index', `${helpZIndex}`);
  };

  connectedCallback() {
    this.#details = this.querySelector('details.help-floating');

    if (!this.#details) return;

    document.addEventListener('click', this.#handleClick);
    document.addEventListener('keydown', this.#handleKeyDown);
    window.addEventListener('resize', this.#handleWindowResize);

    this.#documentObserver = new MutationObserver(this.#handleDocumentMutations);
    this.#documentObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    this.#bannerObserver = new MutationObserver(this.#syncFloatingBannerPosition);
    this.#resizeObserver = new ResizeObserver(this.#syncFloatingBannerPosition);
    this.#syncFloatingBannerPosition();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('resize', this.#handleWindowResize);
    this.#documentObserver?.disconnect();
    this.#bannerObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#documentObserver = null;
    this.#bannerObserver = null;
    this.#resizeObserver = null;
    this.#floatingBanner = null;
    this.#resetFloatingBannerPosition();
  }
}

if (!customElements.get('help-floating-component')) {
  customElements.define('help-floating-component', HelpFloatingComponent);
}
